import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  UploadCloud, Download, Trash2, FileText, FileImage, FileSpreadsheet, File as FileIcon, Loader2, FolderOpen,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconFor = (ct = '', name = '') => {
  const n = name.toLowerCase();
  if (ct.startsWith('image/')) return FileImage;
  if (ct === 'application/pdf' || n.endsWith('.pdf')) return FileText;
  if (n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.xls') || ct.includes('spreadsheet')) return FileSpreadsheet;
  if (ct.startsWith('text/') || n.endsWith('.doc') || n.endsWith('.docx')) return FileText;
  return FileIcon;
};

/** Shared company document library — everyone in the org sees & manages these. */
const RecruiterDocuments = () => {
  const { userProfile } = useAuth();
  const orgId = userProfile?.organizationId;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = () => {
    if (!orgId) return;
    setLoading(true);
    axios
      .get(`${API}/api/documents`, { params: { organizationId: orgId } })
      .then((r) => setDocs(Array.isArray(r.data?.documents) ? r.data.documents : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };

  useEffect(fetchDocs, [orgId]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !orgId) return;
    setUploading(true);
    const name = userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}` : userProfile?.email || '';
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is over 25MB — skipped.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('organizationId', orgId);
        fd.append('uploadedByUserId', userProfile?.id || '');
        fd.append('uploadedByName', name);
        const r = await axios.post(`${API}/api/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.success) setDocs((prev) => [r.data.document, ...prev]);
      }
      toast.success('Upload complete');
    } catch (e) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = (doc) => {
    window.open(`${API}/api/documents/${doc.id}/download?organizationId=${orgId}`, '_blank');
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.originalFilename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/documents/${doc.id}`, { params: { organizationId: orgId } });
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Document deleted');
    } catch (e) {
      toast.error('Could not delete the document.');
    }
  };

  return (
    <RecruiterLayout title="Documents" subtitle="Shared company file library — visible to your whole team">
      <div className="max-w-5xl mx-auto p-4 lg:p-6" data-testid="recruiter-documents-page">
        {/* Upload zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors mb-6 ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'
          }`}
          data-testid="documents-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(e.target.files)}
            data-testid="documents-file-input"
          />
          <span className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
            {uploading ? <Loader2 className="w-6 h-6 text-blue-600 animate-spin" /> : <UploadCloud className="w-6 h-6 text-blue-600" />}
          </span>
          <p className="text-sm font-medium text-slate-700">
            {uploading ? 'Uploading…' : 'Drag & drop files here'}
          </p>
          <p className="text-xs text-slate-400 mt-0.5 mb-3">PDF, Word, images, spreadsheets — up to 25MB each</p>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="documents-upload-btn"
          >
            <UploadCloud className="w-4 h-4 mr-1.5" /> Choose files
          </Button>
        </div>

        {/* List */}
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardContent className="p-2 sm:p-4">
            {loading ? (
              <p className="text-sm text-slate-400 py-10 text-center">Loading documents…</p>
            ) : docs.length === 0 ? (
              <div className="text-center py-14 text-slate-400 flex flex-col items-center gap-2" data-testid="documents-empty">
                <span className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-slate-300" />
                </span>
                <p className="text-sm">No documents yet. Upload your first file above.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="documents-list">
                {docs.map((doc) => {
                  const Icon = iconFor(doc.contentType, doc.originalFilename);
                  return (
                    <div key={doc.id} className="flex items-center gap-3 py-3 px-2 group" data-testid={`document-row-${doc.id}`}>
                      <span className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-slate-500" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{doc.originalFilename}</p>
                        <p className="text-xs text-slate-400">
                          {formatSize(doc.size)}
                          {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ''}
                          {doc.createdAt ? ` · ${new Date(doc.createdAt).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Download"
                        data-testid={`document-download-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                        title="Delete"
                        data-testid={`document-delete-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </RecruiterLayout>
  );
};

export default RecruiterDocuments;
